import {
  type BDCollabDataType,
  type BDCollabDataTypeObserveCallback,
  type BDCollabDataTypeOrigin,
} from './collab-datatype.types';

/**
 * Abstract base class for managing Yjs shared data types.
 *
 * Provides a common interface for integrating different Yjs shared data types
 *
 * @template DataType - The Yjs shared data type (e.g., Map, Array, Text ...).
 * @template DataTypeEvent - To be defined
 */
export abstract class BDCollabDataTypeBase<
  DataType extends BDCollabDataType<DataTypeEvent>,
  DataTypeEvent,
> {
  protected origin: BDCollabDataTypeOrigin;

  private readonly _yType: DataType;
  private _observerCallbacks: BDCollabDataTypeObserveCallback<DataTypeEvent>[] =
    [];

  constructor(yType: DataType, origin: BDCollabDataTypeOrigin) {
    this.origin = origin;
    this._yType = yType;
  }

  /**
   * Observe all events that are created on this type.
   */
  public observe(
    callback: BDCollabDataTypeObserveCallback<DataTypeEvent>,
  ): void {
    this._observerCallbacks.push(callback);
    this._yType.observe(callback);
  }

  /**
   * Unregister an observer function.
   */
  protected unobserve(
    callback: BDCollabDataTypeObserveCallback<DataTypeEvent>,
  ): void {
    if (!this._observerCallbacks.includes(callback)) {
      return;
    }

    this._yType.unobserve(callback);
    this._observerCallbacks = this._observerCallbacks.filter(
      (c) => c !== callback,
    );
  }

  /**
   * Unregister all observers function.
   */
  protected unobserveAll(): void {
    for (const callback of this._observerCallbacks) {
      this._yType.unobserve(callback);
    }

    this._observerCallbacks = [];
  }

  public destroy(): void {
    this.unobserveAll();
  }

  /**
   * Changes that happen inside of a transaction are bundled.
   * This means that the observer fires after the transaction is finished and that
   * all changes that happened inside of the transaction are sent as one message to the other peers.
   */
  protected withTransaction(callback: (...args: unknown[]) => void): void {
    this._yType.doc?.transact(() => callback(), this.origin);
  }
}
